import { PermissionsAndroid, Platform, type Permission } from 'react-native';
import { setPreventsBiomtericsPrompt } from '../redux/uiSlice';

function androidBluetoothPermissions(): Permission[] {
  if (Platform.OS !== 'android') {
    return [];
  }

  const permissions: Permission[] =
    Platform.Version <= 30
      ? [PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION]
      : [];

  if (Platform.Version >= 31) {
    permissions.push(
      PermissionsAndroid.PERMISSIONS.BLUETOOTH_CONNECT,
      PermissionsAndroid.PERMISSIONS.BLUETOOTH_SCAN,
      PermissionsAndroid.PERMISSIONS.BLUETOOTH_ADVERTISE,
    );
  }

  return permissions;
}

function dispatchPreventsBiometricsPrompt(value: boolean) {
  // Lazy to avoid a Store <-> print-core cycle.
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const store = require('../redux/Store').default as {
    dispatch: (action: unknown) => void;
  };
  store.dispatch(setPreventsBiomtericsPrompt(value));
}

/** Check-only. Does not prompt. Use for mount/focus auto-scans. */
export async function hasBluetoothPermissions(): Promise<boolean> {
  if (Platform.OS !== 'android') {
    return true;
  }

  const permissions = androidBluetoothPermissions();
  if (permissions.length === 0) {
    return true;
  }

  const results = await Promise.all(
    permissions.map(permission => PermissionsAndroid.check(permission)),
  );
  return results.every(Boolean);
}

/** Check, then request only if missing. Use from explicit print / scan actions. */
export async function ensureBluetoothPermissions(): Promise<boolean> {
  if (await hasBluetoothPermissions()) {
    return true;
  }
  return requestBluetoothPermissions();
}

/** Runtime request. Call only from an explicit user action (scan / print). */
export async function requestBluetoothPermissions(): Promise<boolean> {
  if (Platform.OS !== 'android') {
    return true;
  }

  try {
    dispatchPreventsBiometricsPrompt(true);

    const permissions = androidBluetoothPermissions();
    if (permissions.length === 0) {
      return true;
    }

    const granted = await PermissionsAndroid.requestMultiple(permissions);

    return permissions.every(
      permission => granted[permission] === PermissionsAndroid.RESULTS.GRANTED,
    );
  } catch (err) {
    console.warn(err);
    return false;
  } finally {
    dispatchPreventsBiometricsPrompt(false);
  }
}
